import { updateConsent } from '../consent';

export interface CookieType {
  id: string;
  name: string;
  description: string;
  required?: boolean;
  defaultValue?: boolean;
  onAccept?: () => void;
  onReject?: () => void;
}

export interface CookieBannerConfig {
  background?: { showBackground?: boolean };
  cookieIcon?: { position?: string };
  cookieTypes?: CookieType[];
  text?: Record<string, Record<string, string>>;
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
}

/**
 * Build the Silktide cookie banner config.
 * @param privacyUrl link to the current site's own privacy policy page
 */
export const createCookieBannerConfig = (
  privacyUrl: string
): CookieBannerConfig => ({
  background: {
    showBackground: true
  },
  cookieIcon: {
    position: 'bottomRight'
  },
  cookieTypes: [
    {
      id: 'necessary',
      name: 'Necessary',
      description:
        '<p>These cookies are necessary for the website to function properly and cannot be switched off. They help with things like logging in, language preferences, and security features.</p>',
      required: true
    },
    {
      id: 'analytics',
      name: 'Analytics',
      description:
        '<p>These cookies help us improve the site by tracking which pages are most popular and how visitors move around the site. We use Google Analytics to understand user behavior.</p>',
      // Deliberately not pre-ticked: consent has to be an affirmative act, and
      // Silktide fires callbacks off the checkbox state, so a checked box makes
      // "Reject non-essential" emit a transient `granted` before `denied`.
      onAccept: () => updateConsent('analytics', true),
      onReject: () => updateConsent('analytics', false)
    },
    {
      id: 'advertising',
      name: 'Advertising',
      description:
        '<p>These cookies provide extra features and personalization to improve your experience. They may be set by us or by partners whose services we use.</p>',
      onAccept: () => updateConsent('advertising', true),
      onReject: () => updateConsent('advertising', false)
    }
  ],
  text: {
    banner: {
      description: `<p>We use cookies on our site to enhance your user experience, provide personalized content, and analyze our traffic. <a href="${privacyUrl}" target="_blank">Cookie Policy.</a></p>`,
      acceptAllButtonText: 'Accept all',
      acceptAllButtonAccessibleLabel: 'Accept all cookies',
      rejectNonEssentialButtonText: 'Reject non-essential',
      rejectNonEssentialButtonAccessibleLabel: 'Reject non-essential',
      preferencesButtonText: 'Preferences',
      preferencesButtonAccessibleLabel: 'Toggle preferences'
    },
    preferences: {
      title: 'Customize your cookie preferences',
      description:
        '<p>We respect your right to privacy. You can choose not to allow some types of cookies. Your cookie preferences will apply across our website.</p>',
      creditLinkText: 'LINKTIVITY Inc.',
      creditLinkAccessibleLabel: 'LINKTIVITY Inc.'
    }
  }
});
